import React, { useEffect, useRef } from "react";

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * A filter panel as a bottom sheet, for phones.
 *
 * Replaces the `DropDown` popover below `sm`. The popover is a fixed-width
 * panel — the checklists ask for 240px, Distance for 260px, inside a `.popover`
 * this project widened to 300px — which is about 77% of a 390px screen.
 *
 * Deliberately NOT built on search/DropDown: that component is shared with
 * /search, which renders eleven of them, so reshaping it to be a sheet would
 * put that page's filtering at risk for no benefit to it.
 *
 * Closing is driven by a real backdrop element rather than a document-level
 * click listener. DropDown's listener decides whether a click was "outside" by
 * comparing `event.target.id` to the trigger's id, which fails whenever the
 * click lands on an inner element — that is a live bug, not a hypothetical, and
 * copying the pattern would inherit it.
 */
export default function FilterSheet({ title, onClose, children }) {
    const sheetRef = useRef(null);

    // `onClose` is an inline arrow in the parent, so its identity changes every
    // render. Reading it through a ref keeps the effect below a mount/unmount
    // effect — depending on it directly would re-run on every render, which
    // would yank focus back to the first control mid-typing and re-capture the
    // "previously focused" element as the sheet's own child.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        const node = sheetRef.current;
        const previouslyFocused = document.activeElement;

        // The sheet claims `aria-modal`, so it has to behave like one: focus
        // moves in, stays in while open, and returns to the trigger on close.
        // Without this, keyboard and screen-reader users tab straight into the
        // results behind a panel that says it is modal.
        const focusables = () =>
            Array.from(node.querySelectorAll(FOCUSABLE))
                .filter((el) => !el.disabled && el.offsetParent !== null);

        (focusables()[0] || node).focus();

        const onKey = (e) => {
            if (e.key === "Escape") {
                onCloseRef.current();
                return;
            }
            if (e.key !== "Tab") return;

            const items = focusables();
            if (!items.length) return;

            const first = items[0];
            const last = items[items.length - 1];

            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };

        // Hold the page still. Without this, a swipe anywhere scrolls the
        // results behind the sheet instead of the sheet's own list.
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        document.addEventListener("keydown", onKey);

        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = previousOverflow;
            if (previouslyFocused && typeof previouslyFocused.focus === "function") {
                previouslyFocused.focus();
            }
        };
    }, []);

    return (
        <>
            <div className="il-filter-sheet-backdrop" onClick={onClose} />
            <div
                ref={sheetRef}
                tabIndex={-1}
                className="il-filter-sheet"
                role="dialog"
                aria-modal="true"
                aria-label={title}
            >
                <div className="il-filter-sheet-head d-flex align-items-center justify-content-between">
                    <strong className="small">{title}</strong>
                    <button
                        type="button"
                        className="il-filter-sheet-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        &times;
                    </button>
                </div>
                <div className="il-filter-sheet-body">{children}</div>
            </div>
        </>
    );
}
